import React, { Fragment, useState } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { Add as AddIcon } from '@material-ui/icons';
import { ErrorComponent, JwlTable, LoadingSpinner } from '../../../../components';

import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { Button } from '@material-ui/core';
import CreateSymbolCollectionDialog from '../SymbolCollectionCreateDialog';
import { GQLParty } from '@jewelutions/core-crm-server-api-client-domain';
import { PlatformNavigation } from '../../navigation/PlatformNavigation';

interface IProps {
  party: GQLParty;
}

const SymbolCollectionsList: React.FC<IProps> = ({ party }) => {
  const [errorComponentState, setErrorComponentState] = useState(false);
  const [showCreateSymbolCollectionDialog, setShowCreateSymbolCollectionDialog] = useState(false);
  const [filteredSymbolCollections, setFilteredSymbolCollections] = useState('');
  const history = useHistory();

  const {
    loading: symbolCollectionLoading,
    error: symbolCollectionError,
    data: symbolCollectionData,
    refetch,
    networkStatus: symbolCollectionNetworkStatus
  } = GQLOperationBuilder.symbolCollection.getSymbolCollectionsByPlatform();

  if (symbolCollectionLoading || symbolCollectionNetworkStatus === NetworkStatus.refetch)
    return <LoadingSpinner />;

  if (symbolCollectionError && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={symbolCollectionError?.stack}
        message={symbolCollectionError.message}
        callback={() => refetch()}
      />
    );

  const handleFilterChange = (event: any) => setFilteredSymbolCollections(event.target.value);

  const getSymbolCollectionRows = () => {
    return GQLOperationBuilder.symbolCollection
      .symbolCollectionArrayFromResponse(symbolCollectionData)
      .filter(symbolCollection => {
        return (
          symbolCollection.title.en!.toLowerCase().includes(filteredSymbolCollections.toLowerCase().trim()) ||
          symbolCollection.description
            .en!.toLowerCase()
            .includes(filteredSymbolCollections.toLowerCase().trim())
        );
      })
      .sort((s1, s2) => s1.title.en!.localeCompare(s2.title.en!))
      .map(symbolCollection => {
        return {
          id: symbolCollection.id,
          cells: [
            symbolCollection.title.en,
            symbolCollection.description.en,
            symbolCollection.symbols.length,
            new Date(Date.parse(symbolCollection.createdAt)).toLocaleString(),
            new Date(Date.parse(symbolCollection.updatedAt)).toLocaleString()
          ]
        };
      });
  };

  const navigateToSymbolCollectionDetails = (symbolCollectionId: string) => {
    history.push(
      PlatformNavigation.symbolCollectionDetails.pathFromParams({ symbolCollectionId: symbolCollectionId })
    );
  };

  return (
    <Fragment>
      <JwlTable
        title="Platform symbol collections list"
        description={`The list of all symbol collections defined in the system.`}
        columns={[
          'Collection name',
          'Collection description',
          'Collection symbols',
          'Created At',
          'Updated At'
        ]}
        rows={getSymbolCollectionRows()}
        onFilterChange={handleFilterChange}
        onTableRowClick={navigateToSymbolCollectionDetails}
        children={
          <Button
            variant="contained"
            color={'primary'}
            onClick={() => setShowCreateSymbolCollectionDialog(true)}
            style={{ marginLeft: '4px' }}
          >
            <AddIcon />
            <span style={{ marginLeft: '4px' }}>Symbol collection</span>
          </Button>
        }
      />
      {showCreateSymbolCollectionDialog && (
        <CreateSymbolCollectionDialog
          dialogId="createSymbolCollectionDialog"
          party={party}
          onClose={val => {
            setShowCreateSymbolCollectionDialog(false);
            refetch();
          }}
        ></CreateSymbolCollectionDialog>
      )}
    </Fragment>
  );
};

export default SymbolCollectionsList;
